import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { CameraPreview, useCamera } from '@/shared/lib/camera';

interface CameraPhotoDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  fileName?: string;
}

const dataUrlToFile = async (dataUrl: string, fileName: string): Promise<File> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
};

export const CameraPhotoDialog = ({ open, onClose, onCapture, fileName = 'photo.jpg' }: CameraPhotoDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    stream,
    error,
    isInitializing,
    videoRef,
    canvasRef,
    capturePhoto,
    startCamera,
    stopCamera,
    facingMode,
    hasMultipleCameras,
    switchCamera,
  } = useCamera({ facingMode: 'user' });

  useEffect(() => {
    if (open && !capturedImage) {
      startCamera();
    }
    if (!open) {
      stopCamera();
      setCapturedImage(null);
    }
  }, [open, capturedImage, startCamera, stopCamera]);

  const handleCapture = useCallback(() => {
    const dataUrl = capturePhoto();
    if (dataUrl) {
      setCapturedImage(dataUrl);
      stopCamera();
    }
  }, [capturePhoto, stopCamera]);

  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    startCamera();
  }, [startCamera]);

  const handleUse = useCallback(async () => {
    if (!capturedImage) {
      return;
    }
    setIsProcessing(true);
    try {
      const file = await dataUrlToFile(capturedImage, fileName);
      onCapture(file);
      setCapturedImage(null);
      onClose();
    } finally {
      setIsProcessing(false);
    }
  }, [capturedImage, fileName, onCapture, onClose]);

  const handleClose = useCallback(() => {
    stopCamera();
    setCapturedImage(null);
    onClose();
  }, [onClose, stopCamera]);

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Take a photo
        <IconButton onClick={handleClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: 'common.black', position: 'relative', minHeight: 360 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {capturedImage ? (
            <Box
              component="img"
              src={capturedImage}
              alt="Captured preview"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <CameraPreview
              videoRef={videoRef}
              canvasRef={canvasRef}
              isInitializing={isInitializing}
              error={error}
              facingMode={facingMode}
            />
          )}

          {!capturedImage && hasMultipleCameras && (
            <IconButton
              onClick={switchCamera}
              aria-label="Switch camera"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.4)',
                color: 'common.white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              }}
            >
              <CameraswitchIcon />
            </IconButton>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', p: 2, gap: 2 }}>
        {capturedImage ? (
          <>
            <Button onClick={handleRetake} startIcon={<ReplayIcon />} disabled={isProcessing}>
              Retake
            </Button>
            <Button onClick={handleUse} variant="contained" disabled={isProcessing}>
              {isProcessing ? 'Saving...' : 'Use photo'}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleCapture}
            variant="contained"
            startIcon={<CameraAltIcon />}
            disabled={!stream || isInitializing || Boolean(error)}
          >
            Capture
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
